import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

/**
 * Formats a profile value in the unit the profile actually measures.
 *
 * This matters more than it looks: the same number is 40 milliseconds in a CPU
 * profile, 40 megabytes in a heap profile and 40 goroutines in a goroutine
 * profile. Rendering all three as "40" would be useless.
 */
export function formatValue(value: number, unit: string): string {
  switch (unit) {
    case 'nanoseconds':
      return formatDuration(value)
    case 'bytes':
      return formatBytes(value)
    case 'count':
    case '':
      return formatCount(value)
    default:
      return `${formatCount(value)} ${unit}`
  }
}

/** Formats a nanosecond duration at a sensible scale. */
export function formatDuration(nanoseconds: number): string {
  const abs = Math.abs(nanoseconds)
  if (abs < 1_000) return `${round(nanoseconds, 0)}ns`
  if (abs < 1_000_000) return `${round(nanoseconds / 1_000, 1)}µs`
  if (abs < 1_000_000_000) return `${round(nanoseconds / 1_000_000, 1)}ms`
  if (abs < 60_000_000_000) return `${round(nanoseconds / 1_000_000_000, 2)}s`
  return `${round(nanoseconds / 60_000_000_000, 1)}min`
}

export function formatBytes(bytes: number): string {
  const abs = Math.abs(bytes)
  if (abs < 1024) return `${round(bytes, 0)} B`

  const units = ['KiB', 'MiB', 'GiB', 'TiB']
  let value = bytes / 1024
  let index = 0
  while (Math.abs(value) >= 1024 && index < units.length - 1) {
    value /= 1024
    index += 1
  }
  return `${round(value, 1)} ${units[index]}`
}

/**
 * The API stores "no limit" as -1 so callers can compare numerically. Printed
 * raw it reads as a broken account: the customer on the most expensive plan is
 * told they have -1 projects. Every quota display goes through here.
 */
export const UNLIMITED = -1

export function formatLimit(value: number): string {
  return value === UNLIMITED ? 'unlimited' : formatCount(value)
}

/** Same rule for byte quotas, which are unlimited on the top plans. */
export function formatByteLimit(value: number): string {
  return value === UNLIMITED ? 'unlimited' : formatBytes(value)
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat().format(Math.round(value))
}

export function formatPercent(value: number, digits = 2): string {
  return `${round(value, digits)}%`
}

/** Formats a signed change, so a regression reads as "+67.8%". */
export function formatSignedPercent(value: number, digits = 1): string {
  const sign = value > 0 ? '+' : ''
  return `${sign}${round(value, digits)}%`
}

export function formatTimestamp(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
}

export function formatRelative(value: string): string {
  return dayjs(value).fromNow()
}

/** Trims a fully qualified function name to what fits in a table cell. */
export function shortFunctionName(name: string): string {
  const lastSlash = name.lastIndexOf('/')
  return lastSlash >= 0 ? name.slice(lastSlash + 1) : name
}

function round(value: number, digits: number): number {
  const factor = 10 ** digits
  return Math.round(value * factor) / factor
}

/** Human label for a sample type, e.g. "Self CPU" rather than "flat". */
export function metricLabel(sampleType: string): string {
  switch (sampleType) {
    case 'cpu':
      return 'CPU'
    case 'inuse_space':
      return 'In-use memory'
    case 'alloc_space':
      return 'Allocated memory'
    case 'inuse_objects':
      return 'In-use objects'
    case 'alloc_objects':
      return 'Allocated objects'
    case 'goroutine':
      return 'Goroutines'
    case 'contentions':
      return 'Contentions'
    case 'delay':
      return 'Delay'
    default:
      return sampleType
  }
}
